// src/systems/encounterSystem.ts — Fase 3: encuentros aleatorios + visibles
import { ENCOUNTER_CONFIG } from '../data/gameConfig.js';
import type { ZoneId } from '../data/gameConfig.js';
import { rollEncounter, pickVisibleSpecies } from '../data/encounters.js';
import type { AsteriaSpecies } from '../entities/asteria.js';
import { getSpecies } from '../data/asterias.js';

export type WildEncounter = {
  species: AsteriaSpecies;
  level: number;
  shiny: boolean;
  source: 'tallgrass'|'visible';
  visibleId?: string; // si viene de visible
};

export class EncounterSystem {
  private stepsSinceEncounter = 0;
  private nextThreshold = this.rollThreshold();
  private cooldown = 0;

  private rollThreshold(): number {
    const avg = ENCOUNTER_CONFIG.tallgrassStepsAvg;
    return Math.floor(avg * (0.6 + Math.random()*0.8)); // 60%-140% de avg
  }

  // Llamar cada vez que el jugador entra en un nuevo tile (no cada frame)
  checkTallgrass(zone: ZoneId, phase: 'amanecer'|'dia'|'atardecer'|'noche', tileId: number): WildEncounter | null {
    if (zone !== 'ruta_aurelia') return null;
    if (tileId !== 12) { // solo hierba alta 12
      // no reset, pero no cuenta
      return null;
    }
    if (this.cooldown > 0) { this.cooldown--; return null; }
    this.stepsSinceEncounter++;
    if (this.stepsSinceEncounter >= this.nextThreshold) {
      const rolled = rollEncounter(zone, phase);
      if (!rolled) return null;
      const sp = getSpecies(rolled.speciesId);
      if (!sp) return null;
      this.stepsSinceEncounter = 0;
      this.nextThreshold = this.rollThreshold();
      this.cooldown = ENCOUNTER_CONFIG.tallgrassCooldownSteps;
      return { species: sp, level: rolled.level, shiny: Math.random() < ENCOUNTER_CONFIG.shinyChance, source: 'tallgrass' };
    }
    return null;
  }

  resetCooldown(): void { this.stepsSinceEncounter=0; this.nextThreshold=this.rollThreshold(); this.cooldown=2; }
}

// Visible overworld Asterias — spawns en Ruta
export type VisibleAsteria = {
  id: string;
  speciesId: string;
  x: number; y: number;
  facing: 'north'|'south'|'east'|'west';
  wanderTick: number;
  level: number;
  behavior: string;
};

export class VisibleSystem {
  private asterias: VisibleAsteria[] = [];
  private respawnAcc = 0;

  getAll(): VisibleAsteria[] { return this.asterias; }

  update(dt: number, zone: ZoneId, phase: 'amanecer'|'dia'|'atardecer'|'noche', map: number[][], npcsPos: {x:number,y:number}[], playerPos:{x:number,y:number}): void {
    if (zone !== 'ruta_aurelia') { this.asterias=[]; return; }
    // respawn
    this.respawnAcc += dt;
    if (this.respawnAcc > ENCOUNTER_CONFIG.visibleRespawnSeconds) {
      this.respawnAcc = 0;
      this.trySpawn(phase, map, npcsPos, playerPos);
    }
    // wander simple: cada visible se mueve aleatoriamente si comportamiento curiosa/huidiza?
    for(const v of this.asterias){
      v.wanderTick -= dt;
      if(v.wanderTick <= 0){
        v.wanderTick = 1.5 + Math.random()*2.5;
        const dirs = [[0,-1],[0,1],[-1,0],[1,0]] as const;
        // comportamiento afecta probabilidad de moverse
        const sp = getSpecies(v.speciesId);
        const isShy = sp?.behavior==='timida' || sp?.behavior==='huidiza';
        const moveChance = isShy ? 0.7 : 0.5;
        if(Math.random() < moveChance){
          const [dx,dy] = dirs[Math.floor(Math.random()*4)];
          const nx = v.x+dx, ny = v.y+dy;
          // check walkable y no colisiona con npc/player a distancia
          if(ny>=0 && ny<map.length && nx>=0 && nx<map[0].length){
            const tile = map[ny][nx];
            const isWalkable = [0,1,8,12,14,17].includes(tile) || tile===12; // hierba, path, flower, tallgrass, plaza etc
            const blockedNpc = npcsPos.some(p=> p.x===nx && p.y===ny);
            const distPlayer = Math.hypot(nx-playerPos.x, ny-playerPos.y);
            const tooClose = distPlayer < 1.5;
            if(isWalkable && !blockedNpc && !tooClose){
              // tímida: huye del jugador si cerca
              if(isShy && distPlayer < 4){
                const awayDx = Math.sign(v.x - playerPos.x), awayDy = Math.sign(v.y - playerPos.y);
                const ax = v.x + awayDx, ay = v.y + awayDy;
                if(ax>=0 && ax<map[0].length && ay>=0 && ay<map.length && [0,1,8,12].includes(map[ay][ax])){
                  v.x=ax; v.y=ay; v.facing = awayDx===1?'east':awayDx===-1?'west': awayDy===1?'south':'north';
                  continue;
                }
              }
              v.x=nx; v.y=ny;
              if(dx===1) v.facing='east'; else if(dx===-1) v.facing='west'; else if(dy===1) v.facing='south'; else v.facing='north';
            }
          }
        }
      }
    }
  }

  private trySpawn(phase: 'amanecer'|'dia'|'atardecer'|'noche', map: number[][], npcsPos:{x:number,y:number}[], playerPos:{x:number,y:number}): void {
    if(this.asterias.length >= ENCOUNTER_CONFIG.visibleMax) return;
    // elegir especie según fase
    const picks = pickVisibleSpecies(phase, 1);
    if(picks.length===0) return;
    const { speciesId, level } = picks[0];
    // buscar posición walkable aleatoria no cerca del jugador ni NPC
    for(let tries=0; tries<40; tries++){
      const x = 6 + Math.floor(Math.random()*(map[0].length-12));
      const y = 6 + Math.floor(Math.random()*(map.length-12));
      const tile = map[y][x];
      if(![0,1,8,12,17].includes(tile)) continue;
      if(npcsPos.some(p=> Math.hypot(p.x-x, p.y-y) < 3)) continue;
      if(Math.hypot(playerPos.x - x, playerPos.y - y) < 5) continue;
      if(this.asterias.some(a=> a.x===x && a.y===y)) continue;
      this.asterias.push({ id:`vis_${Date.now()}_${Math.random().toString(36).slice(2,5)}`, speciesId, x, y, facing:['north','south','east','west'][Math.floor(Math.random()*4)] as any, wanderTick: 1+Math.random()*2, level, behavior: getSpecies(speciesId)?.behavior ?? 'tranquila' });
      break;
    }
  }

  remove(id:string): void { this.asterias = this.asterias.filter(a=> a.id!==id); }
  clear(): void { this.asterias=[]; }
}
